import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseEm } from '../inlineEm';
import { useMarketingCourses } from '../MarketingData';
import { LabSequence } from './LabSequence';
import { CtaLink } from './links';
import { FREE_PAGE } from './content';
import { groupFreePapers, type FreePaper } from './practiceLabPapers';

/**
 * `/free` — the six NSC papers, given away, on a page built for arriving cold.
 *
 * ## Why this is not the catalogue
 *
 * The announce bar is the funnel: the plan is to ask the Department of Basic
 * Education to circulate it, so for many people this page IS Kula Revise, met
 * on a phone from a WhatsApp forward with no context. It used to point at
 * `/courses?q=Practice+Lab`, which returned the right six courses under the
 * wrong page — "Browse Courses / accelerate your career growth", a search box
 * and two filter rows filling the whole first screen, then the papers in
 * enrolment order.
 *
 * This is NOT a shorter scroll: the papers still start around 800px on a
 * phone, much where the first catalogue card did. What changed is what
 * occupies that space — the offer, and a button that opens a question.
 *
 * ## What it reuses, and the one thing it doesn't
 *
 * Almost all of it: `.rv-hero` and `.rv-start` for the two navy bands (both
 * already in the landing surface's dark-regions list in app.css), `.rv-sec` /
 * `.rv-head` / `.rv-btn` for everything between, and `LabSequence` for the
 * worked example — the same four panels the Practice Labs section renders, so
 * there is no second copy of that example to keep true.
 *
 * The exception is the six papers themselves. The landing page's browse window
 * renders the catalogue's own `PublicCourseCard` on the argument that someone
 * should recognise the shelf they are about to land on. That argument does not
 * hold here, because these six descriptions are near-identical by design
 * ("The official NSC November 20XX Paper X Source-based question on…"): six
 * cards is that boilerplate six times over roughly 1,800px of phone scroll,
 * while what actually separates them — paper, question, topic, year — is a
 * row. The cost is a second card design for the same object, and it is the
 * reason `freePapers.ts` derives everything from the live catalogue rather
 * than listing it.
 *
 * ## The page is revise-only
 *
 * Every word of it is about the NSC. `pages/FreePapers.tsx` turns any other
 * theme away rather than this rendering into another tenant's shell.
 */

const PaperRow: React.FC<{ paper: FreePaper }> = ({ paper }) => (
  <Link className="rv-prow" to={`/course/${paper.id}`}>
    {paper.q && (
      <span className="rv-prow-q" aria-hidden="true">
        {paper.q}
      </span>
    )}
    <span className="rv-prow-body">
      <h3>
        {/* The question number is decorative in the column but part of the
            link's name — a screen reader reading "Origins of the Cold War"
            with no question number loses the only thing distinguishing six
            otherwise similar links. */}
        {paper.q && <span className="sr-only">{paper.q}. </span>}
        {paper.topic}
      </h3>
      <p className="rv-prow-meta">
        {paper.year ? `NSC November ${paper.year} · Source-based` : 'Source-based'}
      </p>
    </span>
    <span className="rv-prow-free">Free</span>
    <span className="rv-prow-arrow" aria-hidden="true">
      &rarr;
    </span>
  </Link>
);

/** Copy-the-link button. Resolved from `window.location` so previews and
 *  localhost hand over their own address rather than the production one. */
const ShareRow: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const url = typeof window === 'undefined' ? '' : `${window.location.origin}/free`;

  const copy = useCallback(() => {
    const show = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    };
    // Best-effort: clipboard access can be refused (insecure origin, denied
    // permission) and there is nothing useful to tell someone who can still
    // read the URL sitting next to the button.
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(show, show);
    else show();
  }, [url]);

  return (
    <div className="rv-share">
      <code>{url.replace(/^https?:\/\//, '')}</code>
      <button type="button" onClick={copy}>
        {copied ? FREE_PAGE.passOn.shareDone : FREE_PAGE.passOn.shareLabel}
      </button>
    </div>
  );
};

export const ReviseFreePapers: React.FC = () => {
  const { courses, loaded } = useMarketingCourses();
  const groups = groupFreePapers(courses);
  const first = groups[0]?.papers[0];

  /* The hero and closing buttons open the first paper. Until the fetch lands
     there is nothing to open, so they fall back to the section below — a
     button that goes nowhere is worse than one that goes one step short. */
  const firstHref = first ? `/course/${first.id}` : '#papers';

  return (
    <>
      {/* `data-accent` on every section, the two navy bands included. `--acc`
          falls back to gold, which is the Study Labs hue — correct on the
          landing page, where the hero introduces all three products, and wrong
          here: this page exists to give the Practice Labs away, so it carries
          their exam blue end to end. Without it the kicker above the headline
          came out gold while the section below it was blue. */}
      <section className="rv-hero rv-fhero" data-accent="practice">
        <div className="rv-wrap">
          <p className="rv-kicker">{FREE_PAGE.hero.kicker}</p>
          <h1>{parseEm(FREE_PAGE.hero.headline)}</h1>
          {/* `rv-lede`, not the landing hero's `rv-sub`: this hero has no
              claim line above it and no product names to emphasise, so it
              wants the ledes' ink rather than body grey. Both classes at once
              would leave the winner to stylesheet order. */}
          <p className="rv-lede">{FREE_PAGE.hero.lede}</p>
          <div className="rv-ctas">
            <CtaLink className="rv-btn rv-btn-primary" href={firstHref}>
              {FREE_PAGE.hero.ctaLabel}
            </CtaLink>
            <p className="rv-reassure">
              <b>{FREE_PAGE.hero.reassure}</b>
            </p>
          </div>
        </div>
      </section>

      <section className="rv-sec" id="papers" data-accent="practice">
        <div className="rv-wrap">
          <div className="rv-head">
            <p className="rv-kicker">{FREE_PAGE.papers.kicker}</p>
            <h2>{parseEm(FREE_PAGE.papers.heading)}</h2>
            <p className="rv-lede">{FREE_PAGE.papers.lede}</p>
          </div>

          {/* Nothing while the fetch is in flight. An empty-state on a page
              whose entire job is handing over six courses would be the worst
              possible first frame, and it resolves in one round trip. */}
          {loaded &&
            groups.map((group) => (
              <div className="rv-pgroup" key={group.label ?? 'other'}>
                {group.label && (
                  <div className="rv-pgroup-head">
                    <b>{group.label}</b>
                    <span>{FREE_PAGE.papers.groupNote(group.papers.length)}</span>
                  </div>
                )}
                <div className="rv-papers">
                  {group.papers.map((p) => (
                    <PaperRow key={p.id} paper={p} />
                  ))}
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* The recessed band under the papers, and the page's only route to the
          rest of Kula Revise. Prose, no heading, no cards, and the link is a
          phrase mid-sentence rather than a button — see `onward` in
          content.ts for why it stays that quiet. */}
      <section className="rv-onward" data-accent="practice">
        <div className="rv-wrap">
          <p>
            {FREE_PAGE.onward.before}
            <Link to={FREE_PAGE.onward.linkHref}>{FREE_PAGE.onward.linkLabel}</Link>
            {parseEm(FREE_PAGE.onward.after, (children, key) => (
              <b key={key}>{children}</b>
            ))}
          </p>
        </div>
      </section>

      <section className="rv-sec" data-accent="practice">
        <div className="rv-wrap">
          <div className="rv-head">
            <p className="rv-kicker">{FREE_PAGE.proof.kicker}</p>
            <h2>{parseEm(FREE_PAGE.proof.heading)}</h2>
            <p className="rv-lede">{FREE_PAGE.proof.lede}</p>
          </div>
          <LabSequence lab="practice" />
        </div>
      </section>

      <section className="rv-sec rv-pass" data-accent="practice">
        <div className="rv-wrap">
          <div className="rv-head">
            <p className="rv-kicker">{FREE_PAGE.passOn.kicker}</p>
            <h2>{parseEm(FREE_PAGE.passOn.heading)}</h2>
            <p className="rv-lede">{FREE_PAGE.passOn.lede}</p>
          </div>
          <ul className="rv-tgrid">
            {FREE_PAGE.passOn.points.map((point) => (
              <li key={point.title}>
                <h3>{point.title}</h3>
                <p>{point.body}</p>
              </li>
            ))}
          </ul>
          <ShareRow />
        </div>
      </section>

      {/* `rv-sec` as well as `rv-start`, exactly as the landing page's closing
          band does it (CTASection.tsx). `.rv-start` styles the navy canvas and
          nothing else — all the vertical padding lives on `.rv-sec` — so
          without it the band had none at all and the kicker sat flush on the
          boundary with the oatmeal above. Its `border-top` is harmless here:
          the landing surface zeroes it for `.rv-start`, because the change of
          material already is the boundary.
          Not `rv-band-centre`, which the landing page also carries: this page
          is left-aligned from the hero down, and a centred closing band would
          be the only centred thing on it. */}
      <section className="rv-sec rv-start" data-accent="practice">
        <div className="rv-wrap">
          <div className="rv-head">
            <p className="rv-kicker">{FREE_PAGE.cta.kicker}</p>
            <h2>{parseEm(FREE_PAGE.cta.headline)}</h2>
          </div>
          <div className="rv-ctas">
            <CtaLink className="rv-btn rv-btn-primary" href={firstHref}>
              {FREE_PAGE.cta.buttonLabel}
            </CtaLink>
          </div>
        </div>
      </section>
    </>
  );
};
